import {
  randomBetween,
  seededRandom,
  type PuzzleCore,
} from "./types.ts";

export interface TrigCannonConfig extends Record<string, unknown> {
  gravity: number;
  targetXMin: number;
  targetXMax: number;
  targetYMin: number;
  targetYMax: number;
  angleMin: number;
  angleMax: number;
  powerMin: number;
  powerMax: number;
  tolerance: number;
}

export interface TrigCannonInstance {
  target: { x: number; y: number };
}

export interface TrigCannonSubmission extends Record<string, unknown> {
  angle: number;
  power: number;
}

export function projectileYAtX(
  x: number,
  angleDegrees: number,
  speed: number,
  gravity: number,
) {
  const angle = (angleDegrees * Math.PI) / 180;
  return (
    x * Math.tan(angle) -
    (gravity * x * x) / (2 * speed * speed * Math.cos(angle) ** 2)
  );
}

export const trigCannonCore: PuzzleCore<
  TrigCannonConfig,
  TrigCannonInstance,
  TrigCannonSubmission
> = {
  id: "trig-cannon",
  schemaVersion: 1,
  generate(config, seed) {
    const random = seededRandom(seed);
    return {
      target: {
        x: Math.round(randomBetween(random, config.targetXMin, config.targetXMax)),
        y: Math.round(randomBetween(random, config.targetYMin, config.targetYMax)),
      },
    };
  },
  validate(config, instance, submission) {
    const y = projectileYAtX(
      instance.target.x,
      submission.angle,
      submission.power,
      config.gravity,
    );
    const missDistance = Math.abs(y - instance.target.y);
    const success = missDistance <= config.tolerance;
    return {
      success,
      score: Math.max(0, Math.round(100 - missDistance * 3)),
      feedback: success
        ? "Direct hit. Your velocity components intersected the target."
        : `The projectile crossed the target at ${y.toFixed(1)}m, missing by ${missDistance.toFixed(1)}m.`,
      details: { projectileHeight: y, missDistance },
    };
  },
};
